/*
 * FareRulesCompact.jsx — shared mini fare-rules renderer for flight cards.
 *
 * Sprint-6 · poly-shape: accepts Tripjack normalised, Riya raw, or Offline
 * inline rules.
 *
 * Tripjack (existing): pass `normalized={...}` produced by
 *   GET /api/tripjack/fare-rules/{price_id}
 *   Shape: { routes: [str], by_route: { <route>: {
 *     cancellation: [<slab>...], date_change: [<slab>...],
 *     no_show: <slab|null>, seat_chargeable: <slab|null>,
 *     baggage: {checkin, cabin} | null
 *   } } }
 *
 * Riya (new — X1 adapter): pass the full response body from
 *   GET /api/riya/fare-rule/{flight_id}?track_id=...
 *   Shape: { fare_rules: { FareRuleStock: '6E', FareRuleText: '<long string>' } }
 *
 * Offline (new): pass `{ rules: '<text>' }` produced by `fareRulesRouter`
 * for source='offline' rows.
 *
 * Auto-detects shape; one consumer call site handles all three suppliers.
 */
import React from 'react';

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Return the default slab if present, else the first available.
const pickDefault = (slabs) => {
  if (!Array.isArray(slabs) || slabs.length === 0) return null;
  const def = slabs.find((s) => s.key === 'DEFAULT');
  return def || slabs[0];
};

// Sprint-6 · Riya / offline raw-text adapter. FareRuleText is a multi-line
// human-readable block; we render it as preformatted while the Tripjack
// renderer below produces structured slabs. Both look at home in the same
// modal/popover frame.
const RawRulesBlock = ({ source, stock, text, isTiny }) => {
  if (!text) {
    return (
      <p className="text-[10px] text-gray-500 text-center py-2">
        No fare rules available
      </p>
    );
  }
  return (
    <div className={isTiny ? 'space-y-1' : 'space-y-2'}>
      <div className="flex items-center justify-between">
        <span className={`${isTiny ? 'text-[8px]' : 'text-[10px]'} font-semibold text-gray-500 uppercase tracking-wider`}>
          {source === 'riya' ? 'Carrier Rules' : 'Fare Rules'}
        </span>
        {stock && (
          <span className={`${isTiny ? 'text-[8px] px-1 py-0.5' : 'text-[10px] px-2 py-0.5'} font-semibold text-[#1B375B] bg-[#659A9B]/15 rounded`}>
            {stock}
          </span>
        )}
      </div>
      <pre
        className={`whitespace-pre-wrap break-words font-sans bg-gray-50 rounded ${
          isTiny ? 'p-2 text-[9px]' : 'p-3 text-[11px]'
        } text-gray-700 border border-gray-100 leading-relaxed`}
        data-testid={`fare-rules-raw-${source || 'offline'}`}
      >
        {text}
      </pre>
      <p className={`${isTiny ? 'text-[7px]' : 'text-[9px]'} text-gray-500`}>
        * Fees shown are per passenger per sector. T&amp;C apply.
      </p>
    </div>
  );
};

const FareRulesCompact = (props) => {
  const { normalized, riya, offline, fare_rules, rules, compact = 'default' } = props;
  const isTiny = compact === 'tiny';

  // Sprint-6 · Auto-detect supplier shape so existing call sites keep
  // working without prop migrations:
  //   - Tripjack pages pass `normalized={...}`
  //   - fareRulesRouter Riya response → `{ fare_rules, success, mock }`
  //     callers may either spread it as props or pass `riya={fare_rules}`
  //   - Offline → `{ rules: '<text>' }` (or `offline={text}`)
  if (riya || (fare_rules && (fare_rules.FareRuleText || fare_rules.FareRuleStock))) {
    const fr = riya || fare_rules || {};
    return (
      <RawRulesBlock
        source="riya"
        stock={fr.FareRuleStock}
        text={fr.FareRuleText}
        isTiny={isTiny}
      />
    );
  }
  if (offline || rules) {
    return (
      <RawRulesBlock
        source="offline"
        stock={null}
        text={offline || rules}
        isTiny={isTiny}
      />
    );
  }

  // Tripjack Cat 16 / miscInfo-only (no mini-rule slabs) or API root `farerule` parsed text
  const legs = (normalized?.routes || [])
    .map((r) => normalized?.by_route?.[r])
    .filter(Boolean);
  const hasSlabs = legs.some(
    (leg) =>
      (leg.cancellation || []).length > 0 ||
      (leg.date_change || []).length > 0 ||
      leg.no_show ||
      leg.seat_chargeable
  );
  if (normalized?.unstructured_text && !hasSlabs) {
    return (
      <RawRulesBlock
        source="tripjack"
        stock={null}
        text={normalized.unstructured_text}
        isTiny={isTiny}
      />
    );
  }

  if (!normalized || !normalized.routes || normalized.routes.length === 0) {
    return (
      <p className="text-[10px] text-gray-500 text-center py-2">
        No fare rules available
      </p>
    );
  }

  return (
    <div className={isTiny ? 'space-y-1' : 'space-y-2'}>
      {normalized.routes.map((route) => {
        const leg = normalized.by_route[route];
        if (!leg) return null;

        const cxl = pickDefault(leg.cancellation);
        const dc = pickDefault(leg.date_change);
        const noShow = leg.no_show;
        const seat = leg.seat_chargeable;

        return (
          <div key={route} className={isTiny ? 'space-y-1' : 'space-y-1.5'}>
            {normalized.routes.length > 1 && (
              <div className="text-center">
                <span className={`${isTiny ? 'text-[8px] px-1 py-0.5' : 'text-[10px] px-2 py-1'} font-semibold text-gray-500 bg-gray-100 rounded`}>
                  {route}
                </span>
              </div>
            )}

            {cxl && (
              <div className={`bg-red-50 rounded ${isTiny ? 'p-1.5' : 'p-3'} border border-red-100`}>
                <p className={`${isTiny ? 'text-[8px]' : 'text-xs'} font-bold text-red-900 mb-0.5`}>
                  Cancellation Policy
                </p>
                <div className={`${isTiny ? 'text-[7px]' : 'text-[10px]'} text-red-800 space-y-0.5`}>
                  <div className="flex justify-between">
                    <span>Airline Fee:</span>
                    <span className="font-semibold">{formatINR(cxl.amount)}</span>
                  </div>
                  {cxl.platform_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Platform Fee:</span>
                      <span className="font-semibold">{formatINR(cxl.platform_fee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-0.5 border-t border-red-200">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold">{formatINR(cxl.total)}</span>
                  </div>
                </div>
              </div>
            )}

            {dc && (
              <div className={`bg-orange-50 rounded ${isTiny ? 'p-1.5' : 'p-3'} border border-orange-100`}>
                <p className={`${isTiny ? 'text-[8px]' : 'text-xs'} font-bold text-orange-900 mb-0.5`}>
                  Date Change Policy
                </p>
                <div className={`${isTiny ? 'text-[7px]' : 'text-[10px]'} text-orange-800 space-y-0.5`}>
                  <div className="flex justify-between">
                    <span>Airline Fee:</span>
                    <span className="font-semibold">{formatINR(dc.amount)}</span>
                  </div>
                  {dc.platform_fee > 0 && (
                    <div className="flex justify-between">
                      <span>Platform Fee:</span>
                      <span className="font-semibold">{formatINR(dc.platform_fee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-0.5 border-t border-orange-200">
                    <span className="font-bold">Total:</span>
                    <span className="font-bold">{formatINR(dc.total)}</span>
                  </div>
                </div>
              </div>
            )}

            {noShow && (
              <div className={`bg-gray-100 rounded ${isTiny ? 'p-1.5' : 'p-2.5'}`}>
                <p className={`${isTiny ? 'text-[8px]' : 'text-[10px]'} font-bold text-gray-900 mb-0.5`}>
                  No-Show Policy
                </p>
                <div className={`${isTiny ? 'text-[7px]' : 'text-[10px]'} text-gray-800 flex justify-between`}>
                  <span>No-Show Fee:</span>
                  <span className="font-semibold">{formatINR(noShow.total)}</span>
                </div>
              </div>
            )}

            {seat && (
              <div className={`bg-blue-50 rounded ${isTiny ? 'p-1.5' : 'p-2.5'} border border-blue-100`}>
                <p className={`${isTiny ? 'text-[8px]' : 'text-[10px]'} font-bold text-blue-900 mb-0.5`}>
                  Seat Selection
                </p>
                <div className={`${isTiny ? 'text-[7px]' : 'text-[10px]'} text-blue-800 flex justify-between`}>
                  <span>Seat Charge:</span>
                  <span className="font-semibold">{formatINR(seat.total)}</span>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {normalized?.unstructured_text && hasSlabs && (
        <div className={isTiny ? 'mt-1' : 'mt-2'}>
          <p className={`${isTiny ? 'text-[8px]' : 'text-[10px]'} font-semibold text-gray-600 mb-1`}>
            Full airline fare text
          </p>
          <pre
            className={`whitespace-pre-wrap break-words font-sans bg-amber-50/80 rounded border border-amber-100 text-gray-800 ${
              isTiny ? 'p-2 text-[8px]' : 'p-3 text-[10px]'
            } leading-relaxed`}
            data-testid="fare-rules-tripjack-cat16"
          >
            {normalized?.unstructured_text}
          </pre>
        </div>
      )}

      <div className={`pt-1.5 border-t border-gray-200 space-y-0.5`}>
        <p className={`${isTiny ? 'text-[7px]' : 'text-[9px]'} text-gray-600 leading-snug`}>
          <span className="font-semibold">Note:</span> Airline fees are
          indicative and depend on the actual time of cancellation/re-issue.
        </p>
        <p className={`${isTiny ? 'text-[7px]' : 'text-[9px]'} text-gray-500`}>
          * Fees shown are per passenger per sector. T&amp;C apply.
        </p>
      </div>
    </div>
  );
};

export default FareRulesCompact;
